import React, {
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";

const Download = ({ name, content }) => {
  const downloadTxtFile = () => {
    const element = document.createElement("a");
    const file = new Blob([content], { type: "text/plain" });
    const moment = new Date().toISOString().replace(/\D/g, "").slice(0, 14);

    element.href = URL.createObjectURL(file);
    element.download = `${name}-${moment}-logs.txt`;

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    URL.revokeObjectURL(element.href);
  };

  return (
    <a
      data-loading-effect="pulse"
      data-toggle="cardloading"
      title="Download logs"
      onClick={downloadTxtFile}
    >
      <i className="material-icons">file_download</i>
    </a>
  );
};

const Textarea = styled("textarea")(({ $big }) => ({
  height: $big ? 500 : 300,
}));

const Layout = styled.div`
  margin-top: -35px;

  textarea {
    font-family: "GT-america-mono";
    font-size: 10px;
    line-height: 16px;
    width: 100%;
    padding: 0.25em 0.75em;
    overflow-x: auto;
    white-space: pre;
    resize: vertical;
    outline: none;

    ::-webkit-scrollbar {
      background: var(--scrollbar-color);
      width: 8px;
      height: 8px;
    }

    ::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb-color);
    }
  }

  ul.context-menu {
    text-align: right;
    margin: 0px;

    i {
      font-size: 20px;
      color: #999;
      transition: all 0.5s;

      :hover {
        color: var(--subtitle-color);
      }
    }
  }
`;

const ViewerWrapper = styled.div`
  display: flex;
  align-items: stretch;
`;

const MiniMapContainer = styled.div`
  width: 18px;
  min-width: 18px;
  background: var(--modal-background);
  position: relative;
  cursor: pointer;
  user-select: none;

  svg {
    display: block;
  }
`;

const LINE_HEIGHT = 16;

export const Logs = ({
  name,
  text,
  bqnId,
  big,
  positiveRegex,
  negativeRegex,
}) => {
  const actions = useContext(ActionsContext);

  const textareaRef = useRef(null);
  const minimapRef = useRef(null);
  const draggingRef = useRef(false);

  const [scrollTop, setScrollTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(1);

  const filteredLines = useMemo(() => {
    const linesArray = text.split(/\r?\n/);
    let posReg = null;
    let negReg = null;

    try {
      if (positiveRegex) posReg = new RegExp(positiveRegex, "i");
    } catch (e) {
    }
    try {
      if (negativeRegex) negReg = new RegExp(negativeRegex, "i");
    } catch (e) {
    }

    return linesArray.filter((line) => {
      if (posReg && !posReg.test(line)) return false;
      if (negReg && negReg.test(line)) return false; 
      return true;
    });
  }, [text, positiveRegex, negativeRegex]);

  const filteredText = useMemo(() => filteredLines.join("\n"), [filteredLines]);
  const lines = filteredLines;

  const errorLines = useMemo(() => {
    const result = [];

    lines.forEach((line, index) => {
      if (line.includes("%ERR")) {
        result.push(index);
      }
    });

    return result;
  }, [lines]);

  const updateMetrics = useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    setScrollTop(textarea.scrollTop);
    setClientHeight(textarea.clientHeight);
    setScrollHeight(textarea.scrollHeight);
  }, []);

  useEffect(() => {
    updateMetrics();

    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.addEventListener("scroll", updateMetrics);

    const resizeObserver = new ResizeObserver(() => {
      updateMetrics();
    });

    resizeObserver.observe(textarea);

    return () => {
      textarea.removeEventListener("scroll", updateMetrics);
      resizeObserver.disconnect();
    };
  }, [updateMetrics]);

  useEffect(() => {
    updateMetrics();
  }, [filteredText, updateMetrics]);

  const scrollToLine = (lineNumber) => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.scrollTop = lineNumber * LINE_HEIGHT;

    updateMetrics();
  };

  const scrollToRatio = useCallback(
    (ratio) => {
      const textarea = textareaRef.current;

      if (!textarea) return;

      const clampedRatio = Math.max(0, Math.min(1, ratio));

      textarea.scrollTop =
        clampedRatio * (textarea.scrollHeight - textarea.clientHeight);

      updateMetrics();
    },
    [updateMetrics],
  );

  const moveFromMousePosition = useCallback(
    (clientY) => {
      const minimap = minimapRef.current;

      if (!minimap) return;

      const rect = minimap.getBoundingClientRect();

      const ratio = (clientY - rect.top) / rect.height;

      scrollToRatio(ratio);
    },
    [scrollToRatio],
  );

  const onMiniMapMouseDown = (event) => {
    draggingRef.current = true;

    moveFromMousePosition(event.clientY);

    event.preventDefault();
  };

  const onGlobalMouseMove = useCallback(
    (event) => {
      if (!draggingRef.current) return;

      moveFromMousePosition(event.clientY);
    },
    [moveFromMousePosition],
  );

  const onGlobalMouseUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onGlobalMouseMove);
    window.addEventListener("mouseup", onGlobalMouseUp);

    return () => {
      window.removeEventListener("mousemove", onGlobalMouseMove);
      window.removeEventListener("mouseup", onGlobalMouseUp);
    };
  }, [onGlobalMouseMove, onGlobalMouseUp]);

  useEffect(() => {
    const minimap = minimapRef.current;
    const textarea = textareaRef.current;

    if (!minimap || !textarea) return;

    const handleWheel = (event) => {
      event.preventDefault();

      textarea.scrollTop += event.deltaY;
      textarea.scrollLeft += event.deltaX;

      updateMetrics();
    };

    minimap.addEventListener("wheel", handleWheel, {
      passive: false,
    });

    return () => {
      minimap.removeEventListener("wheel", handleWheel);
    };
  }, [updateMetrics]);

  const viewportHeight =
    scrollHeight > 0 ? (clientHeight / scrollHeight) * 100 : 0;

  const viewportTop =
    scrollHeight > clientHeight
      ? (scrollTop / (scrollHeight - clientHeight)) * (100 - viewportHeight)
      : 0;

  return (
    <Layout>
      <ul className="context-menu header-dropdown m-r--5">
        <Download name={name} content={text} />

        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Refresh"
          onClick={() => actions.send(`do-load-bqn-${bqnId}`)}
        >
          <i className="material-icons">refresh</i>
        </a>
      </ul>

      <ViewerWrapper>
        <Textarea
          ref={textareaRef}
          value={filteredText}
          $big={big}
          readOnly
          onScroll={updateMetrics}
        />

        <MiniMapContainer
          ref={minimapRef}
          onMouseDown={onMiniMapMouseDown}
          style={{
            height: big ? 500 : 300,
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 18 1000"
            preserveAspectRatio="none"
          >
            {errorLines.map((lineIndex) => {
              const y =
                lines.length <= 1 ? 0 : (lineIndex / (lines.length - 1)) * 1000;

              return (
                <line
                  key={lineIndex}
                  x1="1"
                  x2="17"
                  y1={y}
                  y2={y}
                  stroke="#ff0000"
                  strokeWidth="2"
                  style={{
                    cursor: "pointer",
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    scrollToLine(lineIndex);
                  }}
                />
              );
            })}

            <rect
              x="0.5"
              y={(viewportTop / 100) * 1000}
              width="17"
              height={Math.max((viewportHeight / 100) * 1000, 10)}
              fill="rgba(255,255,255,0.05)"
              stroke="var(--subtitle-color)"
              strokeWidth="1"
              pointerEvents="none"
            />
          </svg>
        </MiniMapContainer>
      </ViewerWrapper>
    </Layout>
  );
};
